import React from 'react';
import { twMerge } from 'tailwind-merge';

import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import OptionsModal, {
  IOptionProps,
} from 'components/optionsModal/OptionsModal';
import ButtonComponent from 'components/button/buttonComponent';
import IconRow from './IconRow';
import { getAppointmentDate } from 'utilities/dateHelper';
import StartTimeAndTypeAppointment from './StartTimeAndTypeappointment';
import ReasonAndPaymentBlock from './ReasonAndPaymentBlock';
import { AppointmentHasuraStatus } from '../../const/appointment';
import { UpcomingAppoinment } from '../../interfaces';
import { useAppointmentCardHelper } from './helper/useAppointmentCardHelper';
import moment from 'moment/moment';
import { useCountdown } from 'hooks/useTimer';

interface IUpcomingAppointmentCard extends UpcomingAppoinment {
  appointmentStatus: AppointmentHasuraStatus;
  handleNameClick: () => void;
  options: IOptionProps[];
  handleDetailsClick?: () => void;
}

const UpcomingAppointmentCardMobile: React.FC<IUpcomingAppointmentCard> = ({
  appointmentStatus,
  date,
  end,
  status,
  options,
  name,
  appointmentType,
  appointmentId,
  subjectOfAppointment,
  providerId,
  patientId,
  lastProcessedDate,
  paymentSessionOpenedAt,
  handleNameClick,
  paymentInformation,
  handleDetailsClick,
}) => {
  const {
    loading,
    locale,
    label,
    iconClassNames,
    Icon,
    isNotPaid,
    isJoinable,
    statusClassName,
    notificationClassNames,
    notification,
    opened,
    setOpened,
    hasPastTimeCancelAppointment,
    handleRetryPayment,
    handleJoinAppointment,
  } = useAppointmentCardHelper({
    date,
    appointmentStatus,
    end,
    status,
    appointmentId,
    appointmentType,
    providerId,
    patientId,
    name,
  });

  const { timer } = useCountdown(
    moment(paymentSessionOpenedAt).add(15, 'minutes').toISOString(),
  );

  return (
    <div className="flex flex-col w-full desktop:hidden">
      <div className="p-5 border border-transparent border-b-gray-opacity-15 bg-neutral-100 rounded-tr-xl rounded-tl-xl relative">
        <div className={twMerge('capitalize w-max mb-2.5', statusClassName)}>
          {label || status}
        </div>
        <div className="flex mb-2">
          <span>
            {Icon && notification && <Icon className={iconClassNames} />}
          </span>
          <span className={notificationClassNames}>{notification}</span>
        </div>
        {!hasPastTimeCancelAppointment && (
          <div className="absolute top-2 right-5">
            <OptionsModal
              opened={opened}
              setOpened={setOpened}
              options={options}
              className="!bg-transparent !border-transparent -mr-5"
              optionsMenuClassName="rounded-md w-[16rem] !border-transparent -left-[14rem]"
              optionsClassName="uppercase text-clc-blue text-sm font-bold"
              btnTitleClassName="hidden"
            />
          </div>
        )}
        <div className="inline">
          <span className="text-base font-semibold text-neutral-800 mr-2">
            Appointment with
          </span>
          <ButtonComponent
            type="underline"
            onClick={handleNameClick}
            noUpperCase
            testID={'name-click'}
            containerClassName={'!inline underline'}
            className="ml-1 !w-auto !text-clc-blue contents"
          >
            {name}
          </ButtonComponent>
        </div>
      </div>
      <div className="p-5 pt-2.5 border-b-gray-opacity-15 bg-neutral-100 flex flex-col gap-2 rounded-br-xl rounded-bl-xl">
        <IconRow
          icon={<CalendarIcon className="fill-neutral-800" />}
          text={getAppointmentDate(date)}
        />

        <StartTimeAndTypeAppointment
          type={appointmentType}
          start={date}
          end={String(end)}
        />
        <ReasonAndPaymentBlock
          subjectOfAppointment={subjectOfAppointment}
          appointmentId={appointmentId}
          paymentInformation={paymentInformation}
          appointmentStatus={appointmentStatus}
          startAt={lastProcessedDate}
        />

        {handleDetailsClick && (
          <ButtonComponent
            type="underline"
            className="!text-sm rounded w-full !px-3 ml-auto block"
            desktopFullWidth
            testID={'legal-doc-edit-appointment'}
            onClick={handleDetailsClick}
          >
            {locale?.details || 'Details'}
          </ButtonComponent>
        )}
        {isJoinable && (
          <ButtonComponent
            className="!text-sm rounded desktop:max-w-[6.75rem] !px-3 desktop:ml-auto block"
            desktopFullWidth
            testID={'join-appointment-button'}
            onClick={handleJoinAppointment}
          >
            {locale?.joinNow}
          </ButtonComponent>
        )}

        {isNotPaid ||
          (appointmentStatus ===
            AppointmentHasuraStatus.PendingAuthorizedPayment &&
            moment().diff(paymentSessionOpenedAt, 'minutes') >= 3 && (
              <>
                <div className="flex w-full items-center justify-center mx-auto">
                  <span className="text-neutral-800 font-semibold mr-1">
                    Time to retry:
                  </span>
                  <span className="rounded-full font-semibold py-0.5 px-3 desktop:ml-2.5 border text-bright-green border-bright-green bg-green-50">
                    {timer}
                  </span>
                </div>
                <ButtonComponent
                  className="!text-sm rounded desktop:!w-auto !px-3 block"
                  desktopFullWidth
                  disabled={loading}
                  testID={'retry-payment'}
                  onClick={handleRetryPayment}
                >
                  Retry payment
                </ButtonComponent>
              </>
            ))}
      </div>
    </div>
  );
};

export default UpcomingAppointmentCardMobile;
